import { cn, Divider, Pill, Stack } from 'component-library';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { PlotType } from '@/api/rest/resources/types/plot';
import { PlotTypeIcon } from '@/components';
import { DataGroup, DataStack, DataStackContent, DataStackLabel } from '@/components/DataLabel/DataLabel';
import { NO_RADIUS, useMapRadiusContext } from '@/components/MapOverview/components/MapRadiusContextProvider';
import { EcosystemIntegrityIndexPill } from '@/components/Pill/EcosystemIntegrityIndexPill';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotLocation } from '@/hooks/usePlotLocation';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { isNullish } from '@/utils/booleans';
import { squareMetersToHectares } from '@/utils/plot';

import { useLandscapeViewContext } from '../LandscapeViewContextProvider';

const maxDisplayCropCount = 10;

export const PlotOverview: React.FC = () => {
  const { t } = useTranslation();

  const plot = usePlot().data;

  const areaDisplay = `${useDisplayNumber(squareMetersToHectares(plot.area))} ${t('global.ui.unit.ha')}`;

  const { isLandscapeActive } = useLandscapeViewContext();
  const { radius } = useMapRadiusContext();
  const radiusMeters = isNullish(radius) || radius === NO_RADIUS ? 0 : Number(radius) * 1000;
  const landscapeArea = radiusMeters ? Math.PI * radiusMeters * radiusMeters : 0;
  const landscapeAreaDisplay = `${useDisplayNumber(squareMetersToHectares(landscapeArea))} ${t('global.ui.unit.ha')}`;

  const plotType = plot.type ?? null;

  const location = usePlotLocation({});

  const crops =
    plotType === PlotType.CROPLAND && plot.crops && plot.crops.length > 0
      ? plot.crops.map((crop) => t(`global.crops.${crop}`)).sort()
      : undefined;

  return (
    <PlotOverviewContainer>
      <DataGroup className='grid grid-cols-1 gap-6 md:grid-cols-2 items-start'>
        <DataStack>
          <DataStackLabel>{t('global.plot.mapPopup.labels.plotName')}</DataStackLabel>
          <DataStackContent className=''>{plot.name}</DataStackContent>
        </DataStack>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.eii')}</DataStackLabel>
          <DataStackContent>
            <EcosystemIntegrityIndexPill />
          </DataStackContent>
        </DataStack>
      </DataGroup>

      <Divider />

      <DataGroup className={cn('grid grid-cols-1 gap-6 md:grid-cols-2')}>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.plotSize')}</DataStackLabel>
          <DataStackContent>{areaDisplay}</DataStackContent>
        </DataStack>
        {isLandscapeActive && (
          <DataStack>
            <DataStackLabel>{t('shared.ncaDetail.details.labels.landscapeArea')}</DataStackLabel>
            <DataStackContent>{landscapeAreaDisplay}</DataStackContent>
          </DataStack>
        )}
        <DataStack>
          <DataStackLabel>{t('global.plot.plotLandType')}</DataStackLabel>
          <DataStackContent>
            <Stack direction='row' spacing={1}>
              <PlotTypeIcon type={plotType} size={20} />
              <span>{t(`global.plotTypes.${plotType}`)}</span>
            </Stack>
          </DataStackContent>
        </DataStack>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.location')}</DataStackLabel>
          <DataStackContent>{location}</DataStackContent>
        </DataStack>
        {crops && (
          <DataStack>
            <DataStackLabel>{t('shared.ncaDetail.details.labels.crops')}</DataStackLabel>
            <Stack className='flex flex-row flex-wrap gap-2'>
              {crops.slice(0, maxDisplayCropCount).map((crop, index) => (
                <Pill key={index} size='small' className='whitespace-nowrap bg-warning-light'>
                  {crop}
                </Pill>
              ))}
              {crops.length > maxDisplayCropCount && (
                <Pill key='...' size='small' className='bg-white whitespace-nowrap'>
                  {t('shared.ncaDetail.itemsMoreCount', {
                    count: crops.length - maxDisplayCropCount,
                  })}
                </Pill>
              )}
            </Stack>
          </DataStack>
        )}
      </DataGroup>
    </PlotOverviewContainer>
  );
};

export const PlotOverviewContainer = ({ children }: { children: ReactNode }) => {
  return (
    <div
      className='grid-row grid auto-rows-min items-start rounded-3xl bg-white-100 border border-neutral-black-12'
      data-testid='plot-overview'
    >
      {children}
    </div>
  );
};
